import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { z } from "zod";
import { fetchApiGet, fetchApiPost, fetchApiPut } from "./utils";
import { BaseDbObject } from "../models/db";

const ListQuerySchema = z.object({
  page: z.string().regex(/^\d+$/).transform(Number).optional(),
  limit: z.string().regex(/^\d+$/).transform(Number).optional(),
  file_type: z.enum(["brokerage_daily_statement"]).optional(),
  request_date_from: z.string().optional(), // Format YYYY-MM-DD
  request_date_to: z.string().optional(), // Format YYYY-MM-DD
});

const StatsQuerySchema = z.object({
    trading_account_id: z.string().uuid().optional(),
    start: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "Start date must be in YYYY-MM-DD format").optional(),
    end: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "End date must be in YYYY-MM-DD format"),
    provider_type: z.enum(["dorman", "stonex"]),
});

const StatementsQuerySchema = StatsQuerySchema.extend({
  include_open_positions: z.boolean().default(false),
})

const ReportQueryValidation = z.object({
  start: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "Start date must be in YYYY-MM-DD format").optional(),
  end: z.string().refine((val: string) => /^\d{4}-\d{2}-\d{2}$/.test(val), "End date must be in YYYY-MM-DD format"),
  include_debug_sheet: z.string().optional(), //true or false string
});

const UpdateBodySchema = z.object({
  name: z.string().optional(),
  request_date: z.string().optional(), // Format YYYY-MM-DD
});

const DocumentParamsValidation = z.object({
  fileId: z.string().uuid()
});

export interface BrokerageFile {
  id: string;

  created_at: string;
  updated_at: string;

  Document: {
      key: string;
      bucket: string;
      filename: string;
  }

  name?: string;
  file_type: "brokerage_daily_statement";
  request_date: string;
  provider_type?: "dorman" | "stonex";
  statement_type?: "monthly_statement" | "daily_statement" | "notice_statement";
}

export interface BrokerageFilesListResponse {
  data: BrokerageFile[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BrokerageDocumentResponse {
  link: string;
}

function assembleListQueryParams(filters?: Partial<z.infer<typeof ListQuerySchema>>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  if (filters.page !== undefined) {
    queryParams.append("page", filters.page.toString());
  }

  if (filters.limit !== undefined) {
    queryParams.append("limit", filters.limit.toString());
  }

  if (filters.file_type) {
    queryParams.append("file_type", filters.file_type);
  }

  if (filters.request_date_from) {
    queryParams.append("request_date_from", filters.request_date_from);
  }

  if (filters.request_date_to) {
    queryParams.append("request_date_to", filters.request_date_to);
  }

  return queryParams;
}

export function useListBrokerageFiles(filters?: Partial<z.infer<typeof ListQuerySchema>>) {
  const queryParams = assembleListQueryParams(filters);
  
  return useQuery({
    queryKey: ["brokerage", "files", queryParams.toString()],
    queryFn: () => fetchApiGet<BrokerageFilesListResponse>("dorman/brokerage-statements/files", queryParams),
    retry: 1,
  });
}

export function useGetBrokerageDocumentLink() {
  return async (fileId: string) => {
    const result = await fetchApiGet<BrokerageDocumentResponse>(
      `dorman/brokerage-statements/files/${fileId}/original-document`
    );
    
    return result;
  };
}

export function useUpdateBrokerageFile() {
  const queryClient = useQueryClient();
  
  return async (params: { fileId: string } & z.infer<typeof UpdateBodySchema>) => {
    const { fileId, ...body } = params;
    
    const result = await fetchApiPut<BrokerageFile>(
      `dorman/brokerage-statements/files/${fileId}`,
      body
    );
    
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["brokerage", "files"] });
    }
    
    return result;
  };
}

export interface BrokerageStatsData {
    revisions: ProcessedDataRevision[],
    stats: BrokerageStats[]
}

export interface ProcessedDataRevision extends BaseDbObject {
  item_type: "dorman" | "stonex";

  breakdown_one?: string;
  breakdown_two?: string;
  breakdown_three?: string;
  date?: Date | null;

  unique_id: string;
  revision: number;
}

export interface BrokerageStats extends BaseDbObject {
  brokerage_type: string;
  revision_id: string;

  account_number: string;
  trading_account_id?: string | null;
  statement_date?: Date | null;
  statement_date_raw: string;

  total_equity: Decimal;
  account_value: Decimal;
  cash_value: Decimal;

  is_master_account: boolean;

  TradingAccount: {
    account_id: string;
  }
}

export interface BrokerageStatementRow extends BaseDbObject {
  brokerage_type: string;
  revision_id: string;
  row_type: "base_confirmation" | "open_position" | "reversed_purchase_and_sales" | "base_purchase_and_sales" | "reversed_confirmation";

  account_number: string;
  trading_account_id?: string | null;
  statement_date?: Date | null;
  statement_date_raw: string;

  trade_date: Date;
  settl_card: string;

  long?: number | null;
  short?: number | null;
  net?: number | null;

  description: string;
  price_raw: string;
  debit_credit: Decimal;

  put_or_call?: string | null;
  commodity?: string | null;
  option_month?: string | null;
  strike_price?: string | null;

  cc: string;
  at: string;

  is_reversal: boolean;
  is_summary: boolean;

  price: Decimal;

  TradingAccount?: { account_id: string }
}

// General brokerage file
export interface BrokerageStatsResponse {
  start?: BrokerageStatsData;
  end?: BrokerageStatsData;
}

export interface BrokerageStatementsResponse {
  revisions: ProcessedDataRevision[],
  statements: BrokerageStatementRow[]
}

function assembleStatsQueryParams(filters?: Partial<z.infer<typeof StatsQuerySchema>>) {
  const queryParams = new URLSearchParams();
  if (!filters) {
    return queryParams;
  }

  if (filters.trading_account_id !== undefined) {
    queryParams.append("trading_account_id", filters.trading_account_id);
  }

  if (filters.start && filters.start !== filters.end) {
    queryParams.append("start", filters.start);
  }

  if (filters.end) {
    queryParams.append("end", filters.end);
  }

  if (filters.provider_type) {
    queryParams.append("provider_type", filters.provider_type);
  }

  queryParams.append("include_null_account_ids", "false");

  return queryParams;
}

function assembleStatementQueryParams(filters?: Partial<z.infer<typeof StatementsQuerySchema>>) {
  const queryParams = assembleStatsQueryParams(filters);
  if (filters?.include_open_positions) {
    queryParams.append("include_open_positions", filters.include_open_positions.toString());
  }

  if (filters?.start) {
    queryParams.set("start", filters.start);
  }

  return queryParams;
}

export function useListBrokerageStats(filters?: Partial<z.infer<typeof StatsQuerySchema>>) {
  const queryParams = assembleStatsQueryParams(filters);

  return useQuery({
    queryKey: ["brokerage", "stats", queryParams.toString()],
    queryFn: () => fetchApiGet<BrokerageStatsResponse>("brokerage/brokerage-statements/account/stats", queryParams),
    retry: 1,
  });
}

export function useListBrokerageStatementRows(filters?: Partial<z.infer<typeof StatementsQuerySchema>>) {
  const queryParams = assembleStatementQueryParams(filters);

  return useQuery({
    queryKey: ["brokerage", "statements", queryParams.toString()],
    queryFn: () => fetchApiGet<BrokerageStatementsResponse>("brokerage/brokerage-statements/statement/list", queryParams),
    retry: 1,
  });
}

export function useCreateReport() {
  return useMutation({
    mutationFn: (filters?: Partial<z.infer<typeof StatsQuerySchema>>) => {
      const queryParams = assembleStatsQueryParams(filters);
      return fetchApiPost<{ link: string }>("brokerage/brokerage-statements/statement/report", {}, queryParams);
    },
    onSuccess: () => {},
  });
}


export function useCreateNetVolumeReport() {
  return useMutation({
    mutationFn: (filters?: Partial<z.infer<typeof ReportQueryValidation>>) => {
      const queryParams = assembleStatsQueryParams(filters);
      return fetchApiPost<{ link: string }>("brokerage/brokerage-statements/statement/net-volume-report", {}, queryParams);
    },
    onSuccess: () => {},
  });
}

export function useCreatePnlReport() {
  return useMutation({
    mutationFn: (filters?: Partial<z.infer<typeof ReportQueryValidation>>) => {
      const queryParams = assembleStatsQueryParams(filters);
      return fetchApiPost<{ link: string }>("brokerage/brokerage-statements/statement/pnl-report", {}, queryParams);
    },
    onSuccess: () => {},
  });
}

export function useCreateDailyReconReport() {
  return useMutation({
    mutationFn: (filters?: Partial<z.infer<typeof ReportQueryValidation>>) => {
      const queryParams = assembleStatsQueryParams(filters);
      return fetchApiPost<{ link: string }>("brokerage/brokerage-statements/statement/recon-report", {}, queryParams);
    },
    onSuccess: () => {},
  });
}

export function useCreateEquityReport() {
  return useMutation({
    mutationFn: (filters?: Partial<z.infer<typeof ReportQueryValidation>>) => {
      const queryParams = assembleStatsQueryParams(filters);
      return fetchApiPost<{ link: string }>("brokerage/brokerage-statements/statement/equity-report", {}, queryParams);
    },
    onSuccess: () => {},
  });
}
